import React, { useState, useEffect, useRef } from 'react';
import { Calculator as CalcIcon, Coins, Minimize2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { usePipWindow, PipFrame, isPipSupported } from './PipPortal';

const PIP_BAR_HEIGHT = 40;

const formatNumber = (num) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const useFocusScroll = (calculatorRef) => {
  useEffect(() => {
    const handleFocus = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') {
        setTimeout(() => {
          if (e.target) {
            e.target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
            setTimeout(() => {
              window.scrollBy({ top: -150, behavior: 'smooth' });
            }, 100);
          }
        }, 200);
      }
    };
    const calculator = calculatorRef.current;
    if (calculator) {
      calculator.addEventListener('focusin', handleFocus, true);
      return () => calculator.removeEventListener('focusin', handleFocus, true);
    }
  }, [calculatorRef]);
};

export function LimitCalculatorBase({ percentage, onPercentageChange, onMinimize, onPopout, isMini = false }) {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [initialValue, setInitialValue] = useState('');
  const [refuerzoMode, setRefuerzoMode] = useState(false);
  const [qtyInitial, setQtyInitial] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [buyOverride, setBuyOverride] = useState('');
  const [editingBuy, setEditingBuy] = useState(false);
  const [brokerFee, setBrokerFee] = useState(2);
  const calculatorRef = useRef(null);
  useFocusScroll(calculatorRef);

  const refuerzoFeature = !!(user?.isAdmin || user?.features?.avg_calculator);
  const valInitial = Number(initialValue) || 0;
  const profitTarget = valInitial * (percentage / 100);
  const totalValue = valInitial > 0 ? Math.ceil(valInitial + profitTarget + brokerFee) : 0;

  // Refuerzo (averaging down) calculations
  const q1 = Number(qtyInitial) || 0;
  const p2 = Number(newPrice) || 0;
  const refuerzoReady = refuerzoMode && valInitial > 0 && q1 > 0 && p2 > 0;
  const autoContracts = refuerzoReady ? Math.ceil((q1 * valInitial) / p2) : 0;
  // User can override the contracts to buy to hunt for a lower sale limit
  const contractsToBuy = buyOverride !== '' ? Math.max(0, Math.floor(Number(buyOverride)) || 0) : autoContracts;
  const totalQtyRef = q1 + contractsToBuy;
  const newAverage = refuerzoReady && totalQtyRef > 0
    ? (q1 * valInitial + contractsToBuy * p2) / totalQtyRef
    : 0;
  const totalCostRef = (q1 * valInitial + contractsToBuy * p2) * 100;
  const reducedLimit = refuerzoReady && totalQtyRef > 0
    ? Math.ceil(((totalCostRef * (1 + percentage / 100) + brokerFee) / (totalQtyRef * 100)) * 100) / 100
    : 0;

  return (
    <div
      ref={calculatorRef}
      className="p-4 sm:p-6 w-full bg-white space-y-4 sm:space-y-6 max-w-md mx-auto rounded-xl shadow-lg border border-gray-100"
      data-testid="limit-calculator"
    >
      <div className="flex justify-between items-center border-b pb-2">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CalcIcon className="text-emerald-600" /> {t.calc.limitTitle}
        </h2>
        <div className="flex items-center gap-1">
          {refuerzoFeature && (
            <button
              onClick={() => setRefuerzoMode((v) => !v)}
              className={`px-2 py-1 rounded-lg text-[10px] sm:text-xs font-black uppercase transition-all ${refuerzoMode ? 'bg-purple-600 text-white' : 'text-purple-500 bg-purple-50 hover:bg-purple-100'}`}
              title={language === 'es' ? 'Calculadora de refuerzo (promediar a la baja)' : 'Reinforcement calculator (average down)'}
              data-testid="avg-mode-toggle"
            >
              {language === 'es' ? 'Refuerzo' : 'Reinforce'}
            </button>
          )}
          {!isMini && onMinimize && (
            <button
              onClick={onMinimize}
              className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
              title={language === 'es' ? 'Minimizar (widget flotante al 50%)' : 'Minimize (50% floating widget)'}
              data-testid="minimize-calc-btn"
            >
              <Minimize2 size={18} />
            </button>
          )}
          {!isMini && onPopout && (
            <button
              onClick={() => onPopout(calculatorRef.current)}
              className="px-2 py-1 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all flex flex-col items-center leading-none"
              title={language === 'es' ? 'Abrir en ventana flotante (siempre al frente)' : 'Open in floating window (always on top)'}
              data-testid="popout-calc-btn"
            >
              <span className="text-[9px] font-bold">traer</span>
              <span className="text-[9px] font-bold">al</span>
              <span className="text-[9px] font-bold">Frente</span>
            </button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wider">
            {refuerzoMode ? (language === 'es' ? 'Precio Inicial' : 'Initial Price') : t.calc.currentPrice}
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 sm:text-sm">$</span>
            </div>
            <input
              type="number"
              value={initialValue}
              onChange={(e) => setInitialValue(e.target.value)}
              className="block w-full pl-7 pr-12 py-3 border-gray-300 rounded-lg focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm border"
              placeholder="0.00"
              data-testid="current-price-input"
            />
          </div>
        </div>

        {refuerzoMode && (
          <div className="grid grid-cols-2 gap-4" data-testid="refuerzo-inputs">
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-purple-600 uppercase tracking-wider">
                {language === 'es' ? 'Contratos' : 'Contracts'}
              </label>
              <input
                type="number"
                value={qtyInitial}
                onChange={(e) => setQtyInitial(e.target.value)}
                className="mt-1 block w-full px-3 py-3 border-purple-300 rounded-lg focus:ring-purple-500 focus:border-purple-500 sm:text-sm border bg-purple-50/50"
                placeholder="0"
                data-testid="refuerzo-qty-input"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-purple-600 uppercase tracking-wider">
                {language === 'es' ? 'Precio Nuevo' : 'New Price'}
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-purple-500 sm:text-sm">$</span>
                </div>
                <input
                  type="number"
                  value={newPrice}
                  onChange={(e) => setNewPrice(e.target.value)}
                  className="block w-full pl-7 pr-2 py-3 border-purple-300 rounded-lg focus:ring-purple-500 focus:border-purple-500 sm:text-sm border bg-purple-50/50"
                  placeholder="0.00"
                  data-testid="refuerzo-new-price-input"
                />
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wider">{t.calc.brokerFee}</label>
            <select
              value={brokerFee}
              onChange={(e) => setBrokerFee(Number(e.target.value))}
              className="mt-1 block w-full pl-3 pr-10 py-3 text-base border-gray-300 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm rounded-lg border"
            >
              {[...Array(10)].map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  ${(i + 1).toFixed(2)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wider">{t.calc.targetPercent}</label>
            <select
              value={percentage}
              onChange={(e) => onPercentageChange(Number(e.target.value))}
              className="mt-1 block w-full pl-3 pr-10 py-3 text-base border-gray-300 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm rounded-lg border"
              data-testid="target-percent-select"
            >
              {[...Array(100)].map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}%
                </option>
              ))}
            </select>
          </div>
        </div>

        {refuerzoMode ? (
          <>
            <div className="grid grid-cols-2 gap-4 pt-2" data-testid="refuerzo-results">
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                <p className="text-xs font-bold text-purple-600 uppercase">
                  {language === 'es' ? 'Contratos a comprar' : 'Contracts to buy'}
                </p>
                {refuerzoReady ? (
                  <>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={editingBuy || buyOverride !== '' ? buyOverride : String(autoContracts)}
                      onChange={(e) => setBuyOverride(e.target.value)}
                      onFocus={() => {
                        setEditingBuy(true);
                        if (buyOverride === '') setBuyOverride(String(autoContracts));
                      }}
                      onBlur={() => {
                        setEditingBuy(false);
                        if (buyOverride === '' || Number(buyOverride) === autoContracts) setBuyOverride('');
                      }}
                      className="w-full bg-transparent text-lg font-mono font-semibold text-purple-800 border-b border-dashed border-purple-300 focus:outline-none focus:border-purple-600"
                      data-testid="refuerzo-contracts"
                    />
                    {buyOverride !== '' && Number(buyOverride) !== autoContracts && (
                      <button
                        onClick={() => setBuyOverride('')}
                        className="mt-1 text-[10px] font-bold text-purple-500 hover:text-purple-700 underline"
                        data-testid="refuerzo-contracts-reset"
                      >
                        {language === 'es' ? `Auto: ${autoContracts}` : `Auto: ${autoContracts}`}
                      </button>
                    )}
                  </>
                ) : (
                  <p className="text-lg font-mono font-semibold text-purple-800" data-testid="refuerzo-contracts">-</p>
                )}
              </div>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-xs font-bold text-blue-600 uppercase">
                  {language === 'es' ? 'Nuevo promedio' : 'New average'}
                </p>
                <p className="text-lg font-mono font-semibold text-blue-800" data-testid="refuerzo-average">
                  {refuerzoReady ? `$${newAverage.toFixed(3)}` : '-'}
                </p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-purple-600 rounded-xl shadow-inner text-white">
              <p className="text-sm font-medium opacity-80 uppercase tracking-widest">
                {language === 'es' ? 'Límite reducido' : 'Reduced limit'}
              </p>
              <p className="text-2xl sm:text-3xl font-bold font-mono" data-testid="refuerzo-reduced-limit">
                {refuerzoReady ? `$${reducedLimit.toFixed(2)}` : '-'}
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 pt-2">
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-xs font-bold text-blue-600 uppercase">{percentage}%{t.calc.targetValue}</p>
                <p className="text-lg font-mono font-semibold text-blue-800" data-testid="profit-target">
                  {formatNumber(profitTarget)}
                </p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-emerald-600 rounded-xl shadow-inner text-white">
              <p className="text-sm font-medium opacity-80 uppercase tracking-widest">{t.calc.totalValue}</p>
              <p className="text-2xl sm:text-3xl font-bold font-mono" data-testid="total-value">
                {totalValue}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function ContractCalculatorBase({ onMinimize, onPopout, isMini = false }) {
  const { t, language } = useLanguage();
  const [contractValue, setContractValue] = useState('');
  const [totalBudget, setTotalBudget] = useState('');
  const calculatorRef = useRef(null);
  useFocusScroll(calculatorRef);

  const valContract = Number(contractValue) || 0;
  const valBudget = Number(totalBudget) || 0;
  const quantity = valContract > 0 ? Math.ceil(valBudget / valContract) : 0;

  return (
    <div
      ref={calculatorRef}
      className="p-4 sm:p-6 w-full bg-white space-y-4 sm:space-y-6 max-w-md mx-auto rounded-xl shadow-lg border border-gray-100"
      data-testid="contract-calculator"
    >
      <div className="flex justify-between items-center border-b pb-2">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Coins className="text-amber-600" /> {t.calc.contractsTitle}
        </h2>
        <div className="flex items-center gap-1">
          {!isMini && onMinimize && (
            <button
              onClick={onMinimize}
              className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all"
              title={language === 'es' ? 'Minimizar (widget flotante al 50%)' : 'Minimize (50% floating widget)'}
              data-testid="minimize-contracts-btn"
            >
              <Minimize2 size={18} />
            </button>
          )}
          {!isMini && onPopout && (
            <button
              onClick={() => onPopout(calculatorRef.current)}
              className="px-2 py-1 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all flex flex-col items-center leading-none"
              title={language === 'es' ? 'Abrir en ventana flotante (siempre al frente)' : 'Open in floating window (always on top)'}
              data-testid="popout-contracts-btn"
            >
              <span className="text-[9px] font-bold">traer</span>
              <span className="text-[9px] font-bold">al</span>
              <span className="text-[9px] font-bold">Frente</span>
            </button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wider">{t.calc.contractValue}</label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 sm:text-sm">$</span>
            </div>
            <input
              type="number"
              value={contractValue}
              onChange={(e) => setContractValue(e.target.value)}
              className="block w-full pl-7 pr-12 py-3 border-gray-300 rounded-lg focus:ring-amber-500 focus:border-emerald-500 sm:text-sm border"
              placeholder="0.00"
              data-testid="contract-value-input"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-semibold text-gray-600 uppercase tracking-wider">{t.calc.totalBudget}</label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 sm:text-sm">$</span>
            </div>
            <input
              type="number"
              value={totalBudget}
              onChange={(e) => setTotalBudget(e.target.value)}
              className="block w-full pl-7 pr-12 py-3 border-gray-300 rounded-lg focus:ring-amber-500 focus:border-emerald-500 sm:text-sm border"
              placeholder="0.00"
              data-testid="total-budget-input"
            />
          </div>
        </div>

        <div className="mt-6 p-4 bg-amber-600 rounded-xl shadow-inner text-white">
          <p className="text-sm font-medium opacity-80 uppercase tracking-widest">{t.calc.quantity}</p>
          <p className="text-3xl sm:text-4xl font-bold font-mono" data-testid="contract-quantity">
            {quantity}
          </p>
        </div>
      </div>
    </div>
  );
}

export const useCalculatorPopout = (fallbackUrlParam, fallbackHeight) => {
  const { language } = useLanguage();
  const { pipWindow, openPip } = usePipWindow();

  const onPopout = async (el) => {
    const width = el ? el.offsetWidth : 420;
    const height = (el ? el.offsetHeight : fallbackHeight) + PIP_BAR_HEIGHT;
    if (isPipSupported()) {
      const ok = await openPip({ width, height });
      if (ok) return;
    }
    const baseUrl = window.location.origin + window.location.pathname;
    const win = window.open(`${baseUrl}?popout=${fallbackUrlParam}#/dashboard`, `${fallbackUrlParam}_popout`, `width=${width},height=${height},menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes`);
    if (!win) {
      alert(language === 'es' ? 'Permite las ventanas emergentes para abrir la calculadora flotante' : 'Allow popups to open the floating calculator');
    }
  };

  return { pipWindow, onPopout };
};

export function Calculator({ percentage, onPercentageChange, onMinimize, isMini = false, onPopout: externalPopout = null }) {
  const { t } = useLanguage();
  const internal = useCalculatorPopout('calc', 580);

  return (
    <>
      <LimitCalculatorBase
        percentage={percentage}
        onPercentageChange={onPercentageChange}
        onMinimize={onMinimize}
        onPopout={isMini ? null : (externalPopout || internal.onPopout)}
        isMini={isMini}
      />
      {!externalPopout && internal.pipWindow && (
        <PipFrame pipWindow={internal.pipWindow} title={t.calc.limitTitle} barClass="bg-emerald-600">
          <LimitCalculatorBase percentage={percentage} onPercentageChange={onPercentageChange} isMini />
        </PipFrame>
      )}
    </>
  );
}

export function ContractCalculator({ onMinimize, isMini = false, onPopout: externalPopout = null }) {
  const { t } = useLanguage();
  const internal = useCalculatorPopout('contracts', 480);

  return (
    <>
      <ContractCalculatorBase
        onMinimize={onMinimize}
        onPopout={isMini ? null : (externalPopout || internal.onPopout)}
        isMini={isMini}
      />
      {!externalPopout && internal.pipWindow && (
        <PipFrame pipWindow={internal.pipWindow} title={t.calc.contractsTitle} barClass="bg-amber-600">
          <ContractCalculatorBase isMini />
        </PipFrame>
      )}
    </>
  );
}
